import {
  clearPersistedAuthSession,
  loadPersistedAuthSession,
  resolveIntakeMembership,
  type AuthSession,
  type IntakeMembership,
  type MembershipResolution,
} from "@/modules/auth/public";
import { clearJWT, setJWT, toErrorLogContext } from "@/shared/http";
import { createLogger } from "@/shared/logging";

const logger = createLogger("app/state");

export type AuthState =
  | {
      status: "signedOut";
      session: null;
      membership: null;
    }
  | {
      status: "signedIn";
      session: AuthSession;
      // Intake `/v1/me` 가 확인해 준 조직 소속. BE 에 닿지 못해 확인하지 못했으면 null 이다 — 세션은 살아
      // 있고, 이후 API 가 401/403 으로 거부하면 그때 끝난다.
      membership: IntakeMembership | null;
    };

export type AppState = {
  // 이 앱 인스턴스가 초기화를 시작한 시각. 진단 로그용이며 화면 로직은 읽지 않는다.
  startedAt: string;
  auth: AuthState;
};

export type InitializeAppStateOptions = {
  minimumSplashDurationMs?: number;
};

export type AppStateAction =
  | { type: "app/initialized"; state: AppState }
  | { type: "auth/sessionStarted"; session: AuthSession; membership: IntakeMembership | null }
  | { type: "auth/sessionEnded" };

const signedOut: AuthState = { status: "signedOut", session: null, membership: null };

export function createInitialAppState(startedAt = new Date().toISOString()): AppState {
  return { startedAt, auth: signedOut };
}

export function appStateReducer(state: AppState, action: AppStateAction): AppState {
  switch (action.type) {
    case "app/initialized":
      return action.state;
    case "auth/sessionStarted":
      return {
        ...state,
        auth: { status: "signedIn", session: action.session, membership: action.membership },
      };
    case "auth/sessionEnded":
      return { ...state, auth: signedOut };
  }
}

// 저장된 세션을 복원한 초기 상태를 만든다. 복원 = 저장소 읽기 + Intake `/v1/me` 확인이며, 스플래시 최소
// 표시 시간과 병렬로 진행해 한 번에 뜬다. 여러 번 불려도 결과가 같다(StrictMode 의 effect 재실행 포함).
export async function initializeAppState({
  minimumSplashDurationMs = 350,
}: InitializeAppStateOptions = {}): Promise<AppState> {
  const startedAtMs = Date.now();
  const [auth] = await Promise.all([
    restoreAuthState(),
    waitForMinimumSplashDuration(startedAtMs, minimumSplashDurationMs),
  ]);

  return { startedAt: new Date(startedAtMs).toISOString(), auth };
}

// 세션을 메모리 JWT 로 설치하고 Intake Member 인지 확인한다. 거부되면 JWT 를 되돌린다. 저장소·상태를 어떻게
// 할지는 호출자가 갈래별로 정한다 — 로그인 콜백은 세션을 만들지 않고, 복원은 저장 세션을 지운다.
export async function verifyAuthSession(session: AuthSession): Promise<MembershipResolution> {
  setJWT(session.accessToken);
  const resolution = await resolveIntakeMembership();
  if (resolution.kind === "rejected") {
    clearJWT();
  }

  return resolution;
}

// 복원 · 다른 탭 동기화가 공유하는 판정. 거부 → 저장 세션까지 지우고 로그아웃, 미확인 → 세션은 유지한다.
export function resolveRestoredAuthState(
  session: AuthSession,
  resolution: MembershipResolution,
): AuthState {
  switch (resolution.kind) {
    case "member":
      return { status: "signedIn", session, membership: resolution.membership };
    case "rejected":
      logger.warn(
        "Stored session was rejected by Intake; signing out.",
        resolution.error.toLogContext(),
      );
      clearPersistedAuthSession();
      return signedOut;
    case "unavailable":
      logger.warn(
        "Could not verify Intake membership; keeping the stored session.",
        toErrorLogContext(resolution.error),
      );
      return { status: "signedIn", session, membership: null };
  }
}

async function restoreAuthState(): Promise<AuthState> {
  const session = loadPersistedAuthSession();
  if (session === null) {
    clearJWT();
    return signedOut;
  }

  return resolveRestoredAuthState(session, await verifyAuthSession(session));
}

async function waitForMinimumSplashDuration(
  startedAtMs: number,
  minimumSplashDurationMs: number,
): Promise<void> {
  const remainingMs = minimumSplashDurationMs - (Date.now() - startedAtMs);
  if (remainingMs <= 0) {
    return;
  }
  await new Promise((resolve) => setTimeout(resolve, remainingMs));
}
